import React from 'react';
import { useApp } from '../../context/AppContext';
import { formatCurrency } from '../../utils/helpers';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from 'recharts';

function Overview() {
  const { students, staff, classes, feePayments, exams, marks } = useApp();
  const totalStudents = students.filter(s=>s.status==='active').length;
  const totalStaff = staff.filter(s=>s.status==='active').length;
  const totalCollected = feePayments.reduce((s,p)=>s+Number(p.amount),0);

  const monthlyFees = [
    { month:'Jun', amount:85000 },{ month:'Jul', amount:42000 },
    { month:'Aug', amount:28000 },{ month:'Sep', amount:15000 },
    { month:'Oct', amount:8000 },{ month:'Nov', amount:5000 },
    { month:'Dec', amount:3000 },{ month:'Jan', amount:12000 },
    { month:'Feb', amount:7000 },{ month:'Mar', amount:totalCollected },
  ];

  const completedExam = exams.find(e=>e.status==='completed');
  const subjectPerformance = completedExam ? (() => {
    const subMap = {};
    marks.filter(m=>m.examId===completedExam.id).forEach(m=>{
      if (!subMap[m.subjectId]) subMap[m.subjectId]={ total:0,count:0,id:m.subjectId };
      subMap[m.subjectId].total+=m.marksObtained;
      subMap[m.subjectId].count++;
    });
    return Object.values(subMap).map((s,i)=>({ name:'Subject '+(i+1), avg:s.count>0?Math.round(s.total/s.count):0 }));
  })():[];

  return (
    <div className="space-y-6">
      <div><h1 className="text-2xl font-bold">Reports & Analytics</h1></div>
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        {[['Total Students',totalStudents,'bg-purple-50 text-purple-700'],['Active Staff',totalStaff,'bg-blue-50 text-blue-700'],['Fee Collected',formatCurrency(totalCollected),'bg-green-50 text-green-700'],['Classes',classes.length,'bg-purple-50 text-purple-700']].map(([label,val,cls])=>(
          <div key={label} className={'rounded-xl p-4 text-center border '+cls.replace('text-','border-').replace('-700','-200')}>
            <div className={'text-2xl font-bold '+cls.split(' ')[1]}>{val}</div>
            <div className={'text-sm '+cls.split(' ')[1]}>{label}</div>
          </div>
        ))}
      </div>
      <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-5">
        <h3 className="font-semibold mb-4">Monthly Fee Collection</h3>
        <ResponsiveContainer width="100%" height={220}>
          <BarChart data={monthlyFees}>
            <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
            <XAxis dataKey="month" tick={{fontSize:12}} />
            <YAxis tick={{fontSize:11}} tickFormatter={v=>'₹'+(v/1000).toFixed(0)+'K'} />
            <Tooltip formatter={v=>formatCurrency(v)} />
            <Bar dataKey="amount" name="Fee Collected" fill="#4f46e5" radius={[4,4,0,0]} />
          </BarChart>
        </ResponsiveContainer>
      </div>
      {subjectPerformance.length>0&&(
        <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-5">
          <h3 className="font-semibold mb-4">Average Marks - {completedExam?.name}</h3>
          <ResponsiveContainer width="100%" height={180}>
            <BarChart data={subjectPerformance}>
              <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
              <XAxis dataKey="name" tick={{fontSize:12}} />
              <YAxis domain={[0,100]} tick={{fontSize:11}} />
              <Tooltip />
              <Bar dataKey="avg" name="Avg Marks" fill="#6366f1" radius={[4,4,0,0]} />
            </BarChart>
          </ResponsiveContainer>
        </div>
      )}
      <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-5">
        <h3 className="font-semibold mb-3">Class-wise Summary</h3>
        <table className="w-full text-sm">
          <thead>
            <tr className="bg-gray-50 border-b border-gray-100">
              {['Class','Total Students','Active','Subjects','Class Teacher'].map(h=>(
                <th key={h} className="px-4 py-1.5 text-left font-semibold">{h}</th>
              ))}
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-50">
            {classes.map(cls=>{
              const total = students.filter(s=>s.classId===cls.id).length;
              const active = students.filter(s=>s.classId===cls.id&&s.status==='active').length;
              const teacher = staff.find(s=>s.id===cls.classTeacherId);
              return (
                <tr key={cls.id} className="hover:bg-gray-50">
                  <td className="px-4 py-1.5 font-medium">{cls.name} - {cls.section}</td>
                  <td className="px-4 py-1.5">{total}</td>
                  <td className="px-4 py-1.5 text-green-600 font-medium">{active}</td>
                  <td className="px-4 py-1.5">{(cls.subjectIds||[]).length}</td>
                  <td className="px-4 py-1.5">{teacher?teacher.firstName+' '+teacher.lastName:'-'}</td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}
export default Overview;
